<template>
  <ag-grid-vue
    style="height: 70vh;"
    class="ag-theme-alpine"
    v-bind="agCreditNotes"
    @grid-ready="onGridReady"
    @cell-clicked="onCellClicked"
    @row-double-clicked="onRowDoubleClicked"
  />
</template>

<script>
/* eslint-disable vue/no-unused-components */

import { AgGridVue } from "ag-grid-vue";
import { AG_GRID_LOCALE_ES } from '@ag-grid-community/locale';
import BaseLoader from '@/components/BaseLoader.vue';
import axios from 'axios';
import Formats from "@/mixins/Formats.js";
import AgBadge from '@/components/AgGrid/AgBadge.vue';
import AgIcon from '@/components/AgGrid/AgIcon.vue';
import FilterDate from "@/components/AgGrid/Filters/FilterDate.vue";
import AgGrid from "@/mixins/AgGrid.js";
  export default {
    name: 'TableCreditNotes',
    mixins: [Formats, AgGrid],
    components: {
      AgGridVue,
      BaseLoader,
      AgBadge,
      AgIcon,
      agDateInput: FilterDate,
    },
    data(){
      return {
        salesPersonOptions: null,
        reasons: null,
        downloadingPDF: null,
        downloadingXML: null,
        agCreditNotes: {
          columnDefs: [
            { 
              field: "DocNum", 
              headerName: "# Nota Crédito",
              filter: 'agTextColumnFilter',
              cellClass: 'ag-clickable-cell text-primary font-weight-bold',
              width: 90,
            },
            {
              field: "customer",
              headerName: "Cliente",
              cellClass: "font-weight-bold",
              width: 300,
              filter: 'agTextColumnFilter',
              cellRenderer: function ({data}) {
                return `
                  <div>
                    <span class="font-weight-bold">${data.CardName}</span>
                    <span class="font-weight-normal">${data.CardCode}</span>
                  </div>
                `;
              }
            },
            { 
              field: "U_ReasonForReturn", 
              headerName: "Razón de devolución",
              filter: 'agSetColumnFilter',
              width: 320,
              valueGetter: ({data}) => {
                if(!data || !data.U_ReasonForReturn) return '';
                if(!this.reasons) return data.U_ReasonForReturn;
                const reason = this.reasons.find(x => x.Value === data.U_ReasonForReturn);
                return reason ? reason.Description : data.U_ReasonForReturn;
              },
              filterParams: {
                values: (params) => {
                  if(this.reasons){
                    params.success(this.reasons.map((x) => x.Value));
                  } else {
                    this.watcherReasons = this.$watch(() => this.reasons, () => {
                      params.success(this.reasons.map((x) => x.Value));
                      this.watcherReasons(); // Remove watcher
                    })
                  }
                },
                valueFormatter: this.reasonsFormatter,
                defaultToNothingSelected: true,
                applyMiniFilterWhileTyping: true,
              },
            },
            { 
              field: "DocDate", 
              headerName: "Fecha", 
              width: 80,
              filter: 'agDateColumnFilter',
              valueFormatter: ({value}) => this.dateFormat(value),
            },
            {
              field: "total",
              headerName: "Total",
              width: 120,
              filter: 'agNumberColumnFilter',
              valueFormatter: ({data}) => this.currencyFormat(data.total, data.DocCur),
              headerClass: "ag-right-aligned-header",
              type: 'rightAligned',
            },
            { 
              field: "SlpCode", 
              headerName: "Vendedor",
              width: 120,
              valueGetter: ({data}) => {
                if(!data || data.SlpCode === undefined) return '';
                if(!this.salesPersonOptions) return data.SlpCode;
                const salesPerson = this.salesPersonOptions.find(x => x.SalesEmployeeCode === data.SlpCode);
                return salesPerson ? salesPerson.SalesEmployeeName : data.SlpCode;
              },
              filter: 'agSetColumnFilter',
              filterParams: {
                values: (params) => {
                  if(this.salesPersonOptions){
                    params.success(this.salesPersonOptions.map((x) => x.SalesEmployeeCode));
                  } else {
                    this.watcherSalesPersons = this.$watch(() => this.salesPersonOptions, () => {
                      params.success(this.salesPersonOptions.map((x) => x.SalesEmployeeCode));
                      this.watcherSalesPersons(); // Remove watcher
                    })
                  }
                },
                valueFormatter: this.salesPersonFormatter,
                defaultToNothingSelected: true,
                applyMiniFilterWhileTyping: true,
              },
            },
            {
              field: "Comments",
              headerName: "Comentarios",
              width: 600,
              filter: 'agTextColumnFilter',
            },
            {
              field: "status",
              headerName: "Estatus",
              filter: 'agSetColumnFilter',
              width: 100,
              cellClass: 'd-flex justify-content-center',
              filterParams: {
                values: ['Abierta', 'Cerrada', 'Cancelada'],
                defaultToNothingSelected: true,
                applyMiniFilterWhileTyping: true,
              },
              cellRendererSelector: ({value}) => {
                const colors = {
                  "Abierta": "#5e72e4",
                  "Cerrada": "#adb5bd",
                  "Cancelada": "#f5365c",
                }
                return {
                  component: "AgBadge",
                  params: {
                    text: value,
                    color: colors[value],
                  }
                }
              },
            },
            {
              colId: "pdf",
              headerName: "PDF",
              sortable: false,
              filter: false,
              suppressHeaderMenuButton: true,
              width: 80,
              pinned: 'right',
              cellClass: ({data}) => {
                if (!data.U_TS_ReportID) return '';
                const classList = ['d-flex', 'justify-content-center', 'align-items-center'];
                  if(this.downloadingPDF){
                    if(this.downloadingPDF === data.DocNum){
                      classList.push('opacity-5');
                    }
                  } else {
                    classList.push('ag-clickable-cell');
                  }
                  return classList.join(' ');
              },
              cellRendererSelector: ({data}) => {
                if(!data.U_TS_ReportID) return null;
                return {
                  component: "AgIcon",
                  params: {
                    color: "red",
                    text: "PDF",
                    icon: "fas fa-file-pdf",
                  },
                };
              },
            },
            {
              colId: "xml",
              headerName: "XML",
              sortable: false,
              filter: false,
              suppressHeaderMenuButton: true,
              width: 90,
              pinned: 'right',
              cellClass: (params) => {
                const classList = ['d-flex', 'justify-content-center', 'align-items-center'];
                  if(this.downloadingXML){
                    if(this.downloadingXML === params.data.DocNum){
                      classList.push('opacity-5');
                    }
                  } else if(params.data.U_WebXML){
                    classList.push('ag-clickable-cell');
                  }
                  return classList.join(' ');
              },
              cellRendererSelector: (params) => {
                if(params.data.U_WebXML){
                  return {
                    component: "AgIcon",
                    params: {
                      color: "orange",
                      text: "XML",
                      icon: "fas fa-file-code",
                    },
                  };
                }
              },
            },

          ],
          defaultColDef: {
            menuTabs: ["filterMenuTab"],
            cellDataType: false,
            floatingFilter: true,
            suppressFloatingFilterButton: true,
          },
          rowModelType: "serverSide",
          cacheBlockSize: 30,
          maxBlocksInCache: 10,
          pagination: true,
          paginationPageSize: 1000,
          paginationPageSizeSelector: [100, 500, 1000, 2000],
          loadingOverlayComponent: "BaseLoader",
          suppressCellFocus: true,
          enableCellTextSelection: true,
          localeText: AG_GRID_LOCALE_ES,
          alwaysShowVerticalScroll: true,
        },
      }
    },
    created(){
      this.HANA_URL = process.env.VUE_APP_HANA_URL;
      this.DATABASE = process.env.VUE_APP_DATABASE;
    },
    methods:{
      onGridReady({api}) {
        axios.all([
          axios.get(`/api/sales_persons`),
          axios.get(`/api/user_fields/ReasonForReturn`)
        ]).then(axios.spread((salesPersons, reasons) => {
          this.salesPersonOptions = salesPersons.data.value;
          this.reasons = reasons.data.value[0].ValidValuesMD;
          api.refreshCells({columns: ['SlpCode', 'U_ReasonForReturn']});
        })).catch((error) => {
          console.error(error);
        });

        api.setFilterModel({
          status: { type: "set", values: ["Abierta"] },
        });
        api.setGridOption("serverSideDatasource", this.getServerSideDatasource());
      },
      getServerSideDatasource() {
        return {
          getRows: (params) => {
            const { filterModel, endRow, startRow, sortModel } = params.request;

            //SUBQUERY
            const subQuery = `
              SELECT 
              "DocNum", 
              "DocEntry", 
              "DocDate", 
              "CardCode", 
              "CardName", 
              "DocCur", 
              "SlpCode", 
              "Comments", 
              "U_ReasonForReturn", 
              "U_WebXML",
              "CANCELED",
              "DocStatus",
              "U_TS_ReportID", 
              ("CardCode" || ' ' || "CardName") AS "customer",
              CASE WHEN "DocCur" = 'MXP' THEN "DocTotalSy" ELSE "DocTotalFC" END AS "total",
              CASE WHEN "CANCELED" != 'N' THEN 'Cancelada'
              ELSE
                CASE
                  WHEN "DocStatus" = 'O' THEN 'Abierta'
                  ELSE 'Cerrada'
                END
              END AS "status"

              FROM ${this.DATABASE}."ORIN"
            `;


            //FILTERS
            let filters = ``;
            const filterModelKeys = Object.keys(filterModel);
            if (filterModelKeys.length) {
              const all = filterModelKeys.reduce((acc, key) => {
                if (filterModel[key].operator) {
                  const conditionsArray = filterModel[key].conditions.map((condition) => {
                    return this.formatCondition(condition, key);
                  }
                );
                const toPush = "(" + conditionsArray.join(" " + filterModel[key].operator + " ") + ")";
                acc.push(toPush);
                } else {
                  const condition = this.formatCondition(filterModel[key], key);
                  if(condition){
                    acc.push(condition);
                  }
                }
                return acc;
              }, []);
              if(all.length){
                filters += "WHERE " + all.join(" AND ");
              }
            }

            //PAGINATION
            const pageSize = endRow - startRow;
            const pagination = `LIMIT ${pageSize} OFFSET ${startRow}`;

            //SORTING
            let sorting;
            if (sortModel.length) {
              sorting = `ORDER BY "${sortModel[0].colId}" ${sortModel[0].sort.toUpperCase()}`;
              if(sortModel[0].colId !== "DocNum"){
                sorting += `, "DocNum" DESC`;
              }
            } else {
              sorting = `ORDER BY "DocNum" DESC`;
            }

            //JSON PAYLOADS
            const rowDataJson = {
              query: `
                SELECT *
                FROM (${subQuery})
                ${filters}
                ${sorting}
                ${pagination}
              `
            };
            const totalsJson = {
              query: `
                SELECT COUNT(*) AS "rowCount"
                FROM (${subQuery})
                ${filters}
              `
            };

            // console.log("🟦rowDataJson", rowDataJson.query);
            // console.log("🟦totalsJson", totalsJson.query);

            axios
              .all([
                axios.post(`${this.HANA_URL}/api/hana/freedom`, rowDataJson),
                axios.post(`${this.HANA_URL}/api/hana/freedom`, totalsJson),
              ])
              .then(
                axios.spread((rowDataRes, totalsRes) => {
                  console.log("🟪 DATA", rowDataRes.data);
                  // console.log("🟪 TOTALS", totalsRes.data);
                  params.success({
                    rowData: rowDataRes.data,
                    rowCount: totalsRes.data[0].rowCount,
                  });

                })
              )
              .catch((err) => {
                console.error(err);
                params.fail();
              });
          },
        };
      },
      onCellClicked(params){
        if(params.column.colId === 'DocNum'){
          this.handleClick(params);
        } else if(params.column.colId === 'pdf' && !this.downloadingPDF && params.data.U_TS_ReportID){
          this.handlePDF(params);
        } else if(params.column.colId === 'xml' && !this.downloadingXML && params.data.U_WebXML){
          this.handleXML(params);
        }
      },
      onRowDoubleClicked(params){
        this.handleClick(params);
      },
      handleClick({data}) {
        this.$router.push('/creditNote/' + data.DocNum)
      },
      handlePDF(params){
        this.downloadingPDF = params.data.DocNum;
        params.api.refreshCells({columns: ['pdf']});
          const json = {
            "DocCode": "RIN20015",
            "Pairs": {
              "DocKey@": params.data.DocEntry
            }
          };
          axios.post(`/api/pdf_file`,json)
          .then(res => {
            if(res.data.se_err){
              this.$swal("Error", res.data.se_err.message, "error");
            } else {
              let link = document.createElement('a');
              link.download = `Nota_Credito_${params.data.DocNum}.pdf`;
              link.href =  res.data.base64;
              link.click();
            }
          }).catch(error => {
            console.error(error);
          }).finally(() => {
            this.downloadingPDF = null;
            params.api.refreshCells({columns: ['pdf']});
          });
      },
      handleXML(params){
        let link_xml = document.createElement('a');
        link_xml.download = `Nota_credito_${params.data.DocNum}.xml`;
        link_xml.href = params.data.U_WebXML;
        link_xml.target = '_blank';
        document.body.appendChild(link_xml);
        link_xml.click();
        document.body.removeChild(link_xml);
      },
      reasonsFormatter({value}){
        if(!this.reasons) return value;
        const reason = this.reasons.find(x => x.Value === value);
        return reason ? reason.Description : value;
      },
      salesPersonFormatter({value}){
        if(!this.salesPersonOptions) return value;
        const salesPerson = this.salesPersonOptions.find(x => x.SalesEmployeeCode === value);
        return salesPerson ? salesPerson.SalesEmployeeName : value;
      },
    }
  }
</script>

<style>
.ag-clickable-cell {
  cursor: pointer !important;
}
.ag-clickable-cell:hover {
  background: #e9ecef !important;
}
</style>